import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { UserActions } from '../states/user/user.actions';
import UserState from '../states/user/user.state';
import { UserStateModel } from '../states/user/user.state.model';

/**
 * localStorage key the NGXS storage plugin persists the user state under.
 * It is the state name, since no namespace is configured.
 * @see StatesModule
 */
const USER_STORAGE_KEY = 'user';

/**
 * Keeps every open tab on the same session.
 *
 * The access token is shared through localStorage, but the storage plugin only reads it back
 * at app bootstrap, so a tab that is already open never notices a sign-in or sign-out that
 * happened in another tab. It keeps using its stale token, gets a 401 and shows its own login
 * screen - which is why users had to log in again in each tab. Listening to the `storage` event
 * lets the other tabs pick up the new token, or drop the old one, right away.
 */
@Injectable({ providedIn: 'root' })
export class SessionSyncService implements OnDestroy {

    private listener: (event: StorageEvent) => void;

    constructor(
        private stateStore: Store,
        private router: Router,
        private zone: NgZone) { }

    public init(): void {
        if (this.listener) {
            return;
        }
        this.listener = event => this.onStorageChanged(event);
        window.addEventListener('storage', this.listener);
    }

    ngOnDestroy(): void {
        if (this.listener) {
            window.removeEventListener('storage', this.listener);
            this.listener = null;
        }
    }

    private onStorageChanged(event: StorageEvent): void {
        if (event.key !== USER_STORAGE_KEY || event.storageArea !== localStorage) {
            return;
        }

        const user = SessionSyncService.parseUser(event.newValue);
        const currentTokenId = this.stateStore.selectSnapshot(UserState.getTokenId);

        // The `storage` event fires outside of Angular's zone.
        this.zone.run(() => {
            if (!user) {
                if (currentTokenId) {
                    this.signedOutElsewhere();
                }
            } else if (user.tokenId !== currentTokenId) {
                this.signedInElsewhere(user);
            }
        });
    }

    private signedOutElsewhere(): void {
        this.stateStore.dispatch(new UserActions.Logout());

        if (!this.router.url.includes('/login')) {
            this.router.navigate(['/login'], { queryParams: { returnUrl: this.router.url } });
        }
    }

    private signedInElsewhere(user: UserStateModel): void {
        this.stateStore.dispatch(new UserActions.SetUser(user));

        // Only this tab's login screen is left over from the expired session - send it on its way.
        if (this.router.url.includes('/login')) {
            this.navigateAfterLogin(user);
        }
    }

    /** Mirrors the redirect LoginComponent performs after a successful sign-in. */
    private navigateAfterLogin(user: UserStateModel): void {
        if (user.subscriptionStatus && user.hasPricingPlan === false) {
            this.router.navigateByUrl('register?step=' + (!user.organizationName ? 2 : 3));
            return;
        }

        const queryParams = this.router.parseUrl(this.router.url).queryParams;
        const walmartCallbackUri = queryParams['walmartCallbackUri'];

        if (walmartCallbackUri) {
            this.router.navigate(['settings/sellingchannels'], { queryParams: { walmartCallbackUri } });
        } else {
            this.router.navigateByUrl(queryParams['returnUrl'] || '/');
        }
    }

    private static parseUser(value: string): UserStateModel {
        if (!value || value === 'null' || value === 'undefined') {
            return null;
        }

        try {
            return JSON.parse(value) as UserStateModel;
        } catch {
            return null;
        }
    }
}
